/** Subscription tier — one app-wide signal any feature can gate on.
 *
 *  The tier lives in the shared `tdg-core` Supabase project: a `subscriptions`
 *  row is created automatically for every account at signup, defaulting to the
 *  free tier. This module only ever READS it. Billing, upgrades, and the schema
 *  itself belong to tdg-core, not to this app.
 *
 *  Usage:
 *      import { subscriptionTier, hasTier } from 'services/account/subscription';
 *      if (hasTier('pro')) { …unlock… }
 *      subscriptionTier.subscribe(render, true);   // re-render on change
 *
 *  Gating rule: `subscriptionTier` is `null` while unknown (signed out, still
 *  loading, offline, or no backend configured). Callers must treat null as the
 *  FREE tier — never as "allow everything". `hasTier()` already does.
 */
import { signal } from '../../core/state';
import { activeAuth } from './authAdapter';
import { supabase, supabaseConfigured } from './supabaseClient';

/** Known tiers, cheapest first. Unknown strings from the backend sort last and
 *  are treated as at least free, so a tier added in tdg-core never locks a
 *  paying reader out of this app before it ships an update. */
export const TIER_ORDER = ['free', 'plus', 'pro', 'lifetime'] as const;
export type KnownTier = typeof TIER_ORDER[number];

/** The signed-in reader's tier, or null while unknown. Treat null as free. */
export const subscriptionTier = signal<string | null>(null);

/** True once a real answer has arrived (so UI can tell "loading" from "free"). */
export const tierLoaded = signal<boolean>(false);

function rank(tier: string | null): number {
  if (!tier) return 0;
  const i = (TIER_ORDER as readonly string[]).indexOf(tier);
  // Unknown tier from a newer tdg-core: assume it is at least the top known
  // paid tier rather than denying access to someone who is paying.
  return i === -1 ? TIER_ORDER.length : i;
}

/** Does the reader have AT LEAST this tier? Null/unknown counts as free. */
export function hasTier(min: KnownTier): boolean {
  return rank(subscriptionTier.get()) >= rank(min);
}

/** Convenience: any paid tier at all. */
export function isPaid(): boolean {
  return rank(subscriptionTier.get()) >= rank('plus');
}

/** Read the current user's tier. Safe to call any time; resolves to null when
 *  signed out or when no backend is configured. */
export async function refreshTier(): Promise<void> {
  const uid = activeAuth.current()?.internalId;
  // Never STARTS the accounts library: there is only ever a tier to read for a
  // signed-in reader, and by then it is already here.
  const sb = uid ? await supabase().catch(() => null) : null;
  if (!sb || !uid) {
    subscriptionTier.set(null);
    tierLoaded.set(supabaseConfigured() ? true : false);
    return;
  }
  try {
    // Filter by the owning user rather than trusting RLS alone: if row-level
    // security were ever missing or misconfigured, an unfiltered read would
    // silently return SOMEONE ELSE'S tier, which is the one failure mode worth
    // engineering against here.
    let { data, error } = await sb
      .from('subscriptions').select('tier').eq('user_id', uid).maybeSingle();
    // 42703 = undefined_column. The link column is named something else in
    // tdg-core; fall back to an unfiltered read, which RLS scopes to this user.
    if (error && (error as { code?: string }).code === '42703') {
      ({ data, error } = await sb.from('subscriptions').select('tier').limit(1).maybeSingle());
    }
    if (error) {
      // Never surface a provider error to the reader or the console verbatim.
      console.warn('[subscription] tier lookup failed');
      subscriptionTier.set(null);
      tierLoaded.set(true);
      return;
    }
    const tier = (data as { tier?: string } | null)?.tier ?? 'free';
    subscriptionTier.set(tier);
  } catch {
    console.warn('[subscription] tier lookup failed');
    subscriptionTier.set(null);
  } finally {
    tierLoaded.set(true);
  }
}

/** Wire the tier to the session: it is re-read on every sign-in and cleared on
 *  sign-out. Called once from main.ts's boot(). */
export function initSubscription(): void {
  activeAuth.changes.subscribe((user) => {
    if (!user) {
      subscriptionTier.set(null);
      tierLoaded.set(true);
      return;
    }
    void refreshTier();
  }, true);
}
